import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { productsApi } from '@/api'
import { PRODUCT_DETAIL_COPY, TEA_FORMATS, TEA_PRODUCT_NAMES } from '@/constants/products'
import { getSeoContent } from '@/content/siteContent'
import { useCart } from '@/contexts/CartContext'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useI18n } from '@/hooks'
import { usePageMetadata } from '@/seo/usePageMetadata'
import type { TeaProduct } from '@/types/common'

interface ProductDetailPageProps {
  className?: string
}

const getCopyKey = (product: Pick<TeaProduct, 'key' | 'format'>) =>
  `${product.key}-${product.format}` as keyof typeof PRODUCT_DETAIL_COPY

const getProductDisplayName = (product: TeaProduct, lang: 'en' | 'zh') => {
  if (product.name) {
    return product.name
  }

  const baseName = TEA_PRODUCT_NAMES[product.key]?.[lang] || 'Product'
  const formatName = product.format ? TEA_FORMATS[product.format as keyof typeof TEA_FORMATS]?.[lang] : ''

  if (!formatName || product.format === 'gift-sets') {
    return baseName
  }

  return `${baseName} ${formatName}`
}

const ProductDetailPage = ({ className = '' }: ProductDetailPageProps) => {
  const { productId = '' } = useParams()
  const { addToCart } = useCart()
  const { formatProductPrice } = useCurrency()
  const { lang, fontClass, locale } = useI18n()
  const [product, setProduct] = useState<TeaProduct | null>(null)
  const [loading, setLoading] = useState(true)
  const [quantity, setQuantity] = useState(1)
  const [added, setAdded] = useState(false)
  const [selectedImage, setSelectedImage] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    setAdded(false)
    setQuantity(1)

    void productsApi.getByRouteId(productId)
      .then((item) => {
        if (active) {
          setProduct(item)
          setSelectedImage(item?.images?.[0] ?? item?.image ?? '')
          setLoading(false)
        }
      })
      .catch(() => {
        if (active) {
          setProduct(null)
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [productId])

  const productName = useMemo(() => {
    if (!product) {
      return ''
    }

    return getProductDisplayName(product, lang)
  }, [lang, product])

  const copy = product
    ? PRODUCT_DETAIL_COPY[getCopyKey(product)] ?? PRODUCT_DETAIL_COPY.default
    : PRODUCT_DETAIL_COPY.default
  const galleryImages = product?.images?.length ? product.images : product ? [product.image] : []
  const seo = getSeoContent(lang)
  const pageDescription = product ? copy.summary[lang] : seo.productFallback.description

  usePageMetadata({
    title: product ? `${productName} | CloutySpace` : seo.productFallback.title,
    description: pageDescription,
    path: `/shop/${productId}`,
    locale,
    type: 'product',
    structuredData: product
      ? {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: productName,
          description: pageDescription,
          image: new URL(product.image, window.location.origin).toString(),
          brand: {
            '@type': 'Brand',
            name: 'CloutySpace',
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: product.price.toFixed(1),
            availability: typeof product.stock === 'number' && product.stock <= 0
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            url: `${window.location.origin}/shop/${productId}`,
          },
        }
      : undefined,
  })

  if (loading) {
    return (
      <main className={`min-h-screen bg-white pt-14 ${fontClass} ${className}`}>
        <div className="mx-auto max-w-[1180px] px-5 py-16 md:px-8 lg:px-10">
          <p className="border border-dashed border-neutral-300 px-6 py-4 text-sm text-neutral-500">
            {lang === 'zh' ? '正在載入商品資訊...' : 'Loading product details...'}
          </p>
        </div>
      </main>
    )
  }

  if (!product) {
    return (
      <main className={`min-h-screen bg-white pt-14 ${fontClass} ${className}`}>
        <div className="mx-auto max-w-[900px] px-5 py-16 text-center md:px-8 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-neutral-400">CloutySpace</p>
          <h1 className="mt-4 text-4xl font-medium tracking-tight text-neutral-950">
            {lang === 'zh' ? '找不到商品' : 'Product not found'}
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-neutral-500">
            {lang === 'zh' ? '這個商品可能已下架或網址有誤。' : 'This product may have been removed or the URL is invalid.'}
          </p>
          <Link to="/shop" className="mt-8 inline-flex border-b border-neutral-950 pb-1 text-sm font-medium text-neutral-950">
            {lang === 'zh' ? '回到商店' : 'Back to shop'}
          </Link>
        </div>
      </main>
    )
  }

  const handleAddToCart = () => {
    addToCart(product, quantity)
    setAdded(true)
  }

  return (
    <main className={`min-h-screen bg-white pt-14 text-neutral-900 ${fontClass} ${className}`}>
      <div className="mx-auto max-w-[1240px] px-5 py-12 md:px-8 lg:px-10 lg:py-16">
        <Link to="/shop" className="mb-8 inline-flex text-sm text-neutral-500 transition hover:text-neutral-950">
          {lang === 'zh' ? '返回商店' : 'Back to shop'}
        </Link>

        <section className="grid gap-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(380px,0.78fr)] lg:gap-14">
          <div className="grid gap-4 sm:grid-cols-[88px_minmax(0,1fr)]">
            <div className="order-2 flex gap-3 sm:order-1 sm:flex-col">
              {galleryImages.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  onClick={() => setSelectedImage(image)}
                  className={`h-20 w-20 border bg-transparent p-0 transition ${image === selectedImage ? 'border-neutral-950' : 'border-neutral-200 hover:border-neutral-500'}`}
                  aria-label={`${productName} image ${index + 1}`}
                >
                  <img src={image} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>

            <div className="order-1 overflow-hidden bg-[#f7f6f2] sm:order-2">
              <img
                src={selectedImage || product.image}
                alt={productName}
                className="aspect-[0.86] w-full object-cover"
              />
            </div>
          </div>

          <div className="border-t border-neutral-300 pt-7">
            <p className="text-xs uppercase tracking-[0.26em] text-neutral-400">CloutySpace Tea</p>
            <h1 className="mt-4 text-[clamp(2.7rem,5vw,4.8rem)] font-medium leading-none tracking-tight text-neutral-950">
              {productName}
            </h1>
            <p className="mt-5 text-[1.85rem] font-medium text-neutral-950">{formatProductPrice(product.price, product.priceTwd)}</p>

            <div className="mt-8 space-y-5 text-[1.04rem] leading-[1.9] text-neutral-500">
              <p className="text-xl leading-9 text-neutral-800">{copy.tagline[lang]}</p>
              <p>{copy.intro[lang]}</p>
              <p>{copy.summary[lang]}</p>
            </div>

            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <label className="flex h-14 w-full items-center justify-between border border-neutral-300 px-4 sm:w-36">
                <span className="sr-only">{productName} quantity</span>
                <button
                  type="button"
                  onClick={() => setQuantity(current => Math.max(1, current - 1))}
                  className="border-0 bg-transparent text-xl text-neutral-500 transition hover:text-neutral-950"
                  aria-label="Decrease quantity"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={event => setQuantity(Math.max(1, Number(event.target.value) || 1))}
                  className="w-12 border-0 bg-transparent text-center text-base text-neutral-950 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(current => current + 1)}
                  className="border-0 bg-transparent text-xl text-neutral-500 transition hover:text-neutral-950"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </label>

              <button
                type="button"
                onClick={handleAddToCart}
                className="h-14 flex-1 bg-neutral-950 px-8 text-sm font-medium uppercase tracking-[0.18em] text-white transition hover:bg-neutral-700"
              >
                {added ? (lang === 'zh' ? '已加入購物車' : 'Added to cart') : (lang === 'zh' ? '加入購物車' : 'Add to cart')}
              </button>
            </div>

          </div>
        </section>

        <section className="mt-16 border-t border-neutral-300 pt-8 lg:mt-20">
          <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
            <h2 className="text-[2rem] font-medium tracking-tight text-neutral-950">Description</h2>
            <div className="max-w-3xl space-y-6 text-[1.05rem] leading-[1.95] text-neutral-500">
              <p>{copy.description[lang]}</p>
              <div className="space-y-2 pt-2 text-neutral-700">
                {copy.specs.map(spec => (
                  <p key={spec}>{spec}</p>
                ))}
              </div>
              <p>{lang === 'zh' ? '我們的茶品皆源自台灣，從茶園直接來到你的日常。' : 'Our tea products all originated from Taiwan. Straight from our farm to you.'}</p>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}

export default ProductDetailPage
